import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

/* ===== API base (as requested) ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* Minimal HTTP helper (local to this file) */
async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

const api = {
  listCategories: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/categories${qs}`);
  },
  createCategory: (payload) =>
    http(`/api/categories`, { method: "POST", data: payload }),
  updateCategory: (id, payload) =>
    http(`/api/categories/${id}`, { method: "PATCH", data: payload }),
  deleteCategory: (id) => http(`/api/categories/${id}`, { method: "DELETE" }),
};

/* ===== Theme (match AdminLayout) ===== */
const theme = {
  brand: "#3dccc7",
  accent: "#ff914d",
  white: "#f1f1f1",
  subtext: "#d6d9de",
  card: "#0f1b2d",
  border: "#1e293b",
  bg: "#0b1320",
};

/* ===== Layout & Table UI ===== */
const Wrap = styled.div`
  display: grid;
  gap: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(18px, 2.4vw, 22px);
  font-weight: 800;
  color: ${theme.white};
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid ${theme.border};
  background: ${theme.accent};
  color: #111;
  font-weight: 800;
  cursor: pointer;
  &:hover { filter: brightness(1.05); }
`;

const TableShell = styled.div`
  border: 1px solid ${theme.border};
  background: linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));
  border-radius: 16px;
  overflow: hidden;
`;

const Scroll = styled.div`
  max-width: 100%;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 800px;
`;

const Thead = styled.thead`
  background: linear-gradient(180deg, #0f1b2d, #0b1626);
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Th = styled.th`
  text-align: left;
  padding: 14px 16px;
  font-size: 12px;
  color: ${theme.subtext};
  text-transform: uppercase;
  border-bottom: 1px solid ${theme.border};
  white-space: nowrap;

  &:first-child { padding-left: 18px; }
  &:last-child  { padding-right: 18px; }
`;

const Tr = styled.tr`
  transition: background 0.12s ease;
  &:nth-child(even) { background: rgba(255,255,255,0.02); }
  &:hover { background: rgba(255,255,255,0.05); }
`;

const Td = styled.td`
  padding: 14px 16px;
  font-size: 14px;
  color: ${theme.white};
  border-bottom: 1px dashed ${theme.border};
  vertical-align: middle;

  &:first-child { padding-left: 18px; }
  &:last-child  { padding-right: 18px; }
`;

const ActionBtn = styled.button`
  padding: 6px 10px;
  margin-right: 6px;
  border-radius: 8px;
  border: 1px solid ${theme.border};
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  background: ${({ variant }) => (variant === "edit" ? theme.brand : "rgba(255,0,0,0.7)")};
  color: #fff;
  &:hover {
    filter: brightness(1.1);
  }
`;

/* ===== Modal ===== */
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(2px);
  display: grid; place-items: center;
  z-index: 50;
`;

const Modal = styled.div`
  width: min(520px, 92vw);
  border-radius: 16px;
  border: 1px solid ${theme.border};
  background: ${theme.card};
  box-shadow: 0 30px 70px rgba(0,0,0,0.45);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid ${theme.border};
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: ${theme.white};
`;

const CloseBtn = styled.button`
  width: 34px; height: 34px;
  display: grid; place-items: center;
  border-radius: 10px;
  border: 1px solid ${theme.border};
  background: rgba(255,255,255,0.06);
  color: ${theme.white};
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.1); }
`;

const ModalBody = styled.div`
  padding: 16px;
  display: grid; gap: 14px;
`;

const Label = styled.label`
  font-size: 13px;
  color: ${theme.subtext};
`;

const Input = styled.input`
  width: 100%;
  background: ${theme.bg};
  color: ${theme.white};
  border: 1px solid ${theme.border};
  border-radius: 10px;
  padding: 10px 12px;
  outline: none;
  &:focus {
    border-color: ${theme.brand};
    box-shadow: 0 0 0 2px rgba(61,114,148,0.25);
  }
`;

const ModalFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid ${theme.border};
`;

const Secondary = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid ${theme.border};
  background: rgba(255,255,255,0.06);
  color: ${theme.white};
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.1); }
`;

const Primary = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid ${theme.border};
  background: ${theme.accent};
  color: #111;
  font-weight: 800;
  cursor: pointer;
  &:hover { filter: brightness(1.05); }
`;

/* ===== Tiny Toasts (no extra deps) ===== */
const ToastWrap = styled.div`
  position: fixed;
  right: 16px;
  bottom: 16px;
  display: grid;
  gap: 10px;
  z-index: 1000;
`;

const ToastItem = styled.div`
  background: ${props => (props.variant === "error" ? "#b91c1c" : "#16a34a")};
  color: #fff;
  border-radius: 10px;
  padding: 10px 14px;
  border: 1px solid ${theme.border};
  box-shadow: 0 10px 30px rgba(0,0,0,0.35);
  min-width: 220px;
`;

/* click-outside helper */
function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler();
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

export default function PromptCategories() {
  /* Data */
  const [rows, setRows] = useState([]); // {_id, name, slug, totalPrompts}
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  /* Modal state */
  const [open, setOpen] = useState(false);
  const [catName, setCatName] = useState("");
  const [editId, setEditId] = useState(null);

  /* Toasts */
  const [toasts, setToasts] = useState([]); // {id,msg,variant}
  const pushToast = (msg, variant = "success") => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, msg, variant }]);
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 2800);
  };

  /* Close modal on outside click / ESC */
  const modalRef = useRef(null);
  useOnClickOutside(modalRef, () => setOpen(false));
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  /* Fetch categories */
  const fetchRows = async () => {
    try {
      setLoading(true);
      const res = await api.listCategories({ page: 1, pageSize: 200 });
      setRows(res.data); // backend sends totalPrompts
      setErr("");
    } catch (e) {
      setErr(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  /* Submit (create or update) */
  const onSubmit = async (e) => {
    e.preventDefault();
    const name = catName.trim();
    if (!name) { setOpen(false); return; }

    try {
      if (editId) {
        const res = await api.updateCategory(editId, { name });
        setRows((prev) => prev.map((r) => (r._id === editId ? res.data : r)));
        pushToast("Category updated");
      } else {
        const res = await api.createCategory({ name });
        setRows((prev) => [res.data, ...prev]);
        pushToast("Category created");
      }
      setCatName("");
      setEditId(null);
      setOpen(false);
    } catch (e) {
      setErr(e.message || "Action failed");
      pushToast(e.message || "Action failed", "error");
    }
  };

  /* Edit / Delete */
  const handleEdit = (row) => {
    setCatName(row.name);
    setEditId(row._id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await api.deleteCategory(id);
      setRows((prev) => prev.filter((r) => r._id !== id));
      pushToast("Category deleted");
    } catch (e) {
      pushToast(e.message || "Delete failed", "error");
    }
  };

  return (
    <Wrap>
      <TitleRow>
        <Title>Prompt Categories</Title>
        <AddBtn onClick={() => { setCatName(""); setEditId(null); setOpen(true); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add New Category
        </AddBtn>
      </TitleRow>

      <TableShell>
        <Scroll>
          <Table>
            <Thead>
              <Tr>
                <Th>Id</Th>
                <Th>Category name</Th>
                <Th>Total no. of prompts curated for this category</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <tbody>
              {loading ? (
                <Tr><Td colSpan={4}>Loading...</Td></Tr>
              ) : err ? (
                <Tr><Td colSpan={4} style={{ color: "#ffb4b4" }}>{err}</Td></Tr>
              ) : rows.length === 0 ? (
                <Tr><Td colSpan={4}>No categories yet.</Td></Tr>
              ) : (
                rows.map((r, idx) => (
                  <Tr key={r._id}>
                    <Td>{idx + 1}</Td>
                    <Td>{r.name}</Td>
                    <Td>{r.totalPrompts ?? 0}</Td>
                    <Td>
                      <ActionBtn variant="edit" onClick={() => handleEdit(r)}>Edit</ActionBtn>
                      <ActionBtn variant="delete" onClick={() => handleDelete(r._id)}>Delete</ActionBtn>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </Table>
        </Scroll>
      </TableShell>

      {open && (
        <Overlay>
          <Modal ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="addCatTitle">
            <ModalHeader>
              <ModalTitle id="addCatTitle">{editId ? "Edit Category" : "Add New Category"}</ModalTitle>
              <CloseBtn onClick={() => setOpen(false)} aria-label="Close">✕</CloseBtn>
            </ModalHeader>

            <form onSubmit={onSubmit}>
              <ModalBody>
                <div>
                  <Label htmlFor="catName">Category Name</Label>
                  <Input
                    id="catName"
                    placeholder="e.g. SEO, Cold Email, Brainstorming"
                    value={catName}
                    onChange={(e) => setCatName(e.target.value)}
                    autoFocus
                  />
                </div>
              </ModalBody>

              <ModalFooter>
                <Secondary type="button" onClick={() => setOpen(false)}>Close</Secondary>
                <Primary type="submit">Submit</Primary>
              </ModalFooter>
            </form>
          </Modal>
        </Overlay>
      )}

      {/* Toasts */}
      <ToastWrap>
        {toasts.map((t) => (
          <ToastItem key={t.id} variant={t.variant}>{t.msg}</ToastItem>
        ))}
      </ToastWrap>
    </Wrap>
  );
}
